import { useState, useEffect, useLayoutEffect, useRef, Fragment } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { readSidebarCollapsed, writeSidebarCollapsed } from './navGroups';
import { Dialog, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '@/stores/authStore';
import { useAuth } from '@/hooks/useAuth';
import { useIdleLogout } from '@/hooks/useIdleLogout';
import { useNarrow } from '@/hooks/useNarrow';
import { clockReading, orgTimeZone, setDeskTimeZone } from '@/utils/timezone';
import Sidebar from './Sidebar';
import { bottomNavFor } from './bottomNav';
import UserMenu from './UserMenu';
import QuickActions from './QuickActions';
import GlobalSearch from './GlobalSearch';
import KeyboardShortcuts from './KeyboardShortcuts';
import NotificationCentre from './NotificationCentre';
import PendingQueueBadge from './PendingQueueBadge';
import SiteScopeChip from './SiteScopeChip';
import { FmGreeting, FmSitePicker } from '@/components/fm/FmTopBar';
import { FmEmergencyButton } from '@/components/fm/EmergencyModal';
import { isFmExperience } from '@/config/fmExperience';
import OnboardingTour from '@/components/Onboarding/OnboardingTour';
import EmployeeWelcome from '@/components/MyDay/EmployeeWelcome';
import { isEmployeeExperience } from '@/config/employeeExperience';
import { isTechShell } from '@/config/techExperience';
import { RECEPTION_HOME, isReceptionShell } from '@/config/receptionExperience';
import DeskTopBar from '@/components/Reception/DeskTopBar';
import PendingQueueLine from '@/components/Common/PendingQueueLine';
import { useTechHome } from '@/components/Tech/techApi';
import RunningJobBar from '@/components/Tech/RunningJobBar';
import TechMeEffects from '@/components/Tech/TechMeEffects';
import { useTechLocked } from '@/components/Tech/lockState';
import { TechConnectionPill, TechIncidentButton } from '@/components/Tech/TechTopBar';
import { TechSos } from '@/components/Tech/SosButton';
import VerifyPasswordBanner from '@/components/Common/VerifyPasswordBanner';
import ImpersonationBanner from './ImpersonationBanner';
import EmergencyBanner from '@/components/Emergency/EmergencyBanner';
import NoticePrompt from '@/components/Privacy/NoticePrompt';
import PushPromptBar from '@/components/Notifications/PushPromptBar';
import SupportModal, { SupportHeaderButton } from '@/components/Common/SupportModal';
// SupportModal IS mounted, below, for every signed-in user (Bug_84). This line
// used to say it was not imported on purpose — true once, stale since the
// support inbox gave it a recipient. See the note at the top of the component.

// The mobile bottom nav is components/Layout/bottomNav.ts (Employee B3): it is
// filtered by the same canOfferInNav as the sidebar.

// ---------------------------------------------------------------------------
// AppLayout
// ---------------------------------------------------------------------------

/**
 * The top-bar clock, in the ORGANISATION's timezone, named.
 *
 * It was `useISTClock` once (hardcoded Asia/Kolkata, captioned "IST"), then
 * followed the browser. 🔴 DSH-025: following the browser made it the one
 * thing on the page in a different zone — every timestamp goes through
 * utils/dateDisplay, which renders in orgTimeZone(), so an IST laptop on a
 * UTC organisation read 09:20 in the corner and 03:50 in the delivery log for
 * the same minute. The product rule is the organisation's zone; the clock
 * follows it and names it, so nobody has to guess which zone they are reading.
 *
 * orgTimeZone() falls back to the browser when the organisation has set none,
 * which is the same fallback every timestamp uses.
 */
function useOrgClock() {
  const read = () => ({ ...clockReading(new Date(), orgTimeZone()), iana: orgTimeZone() });

  const [clock, setClock] = useState(read);

  useEffect(() => {
    const id = setInterval(() => setClock(read()), 30000);
    return () => clearInterval(id);
  }, []);

  return clock;
}

export default function AppLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const narrowScreen = useNarrow();
  const location = useLocation();
  // Called for its effect: it loads /auth/me into the store. The menu reads
  // the user itself (UserMenu).
  useAuth();
  const navUser = useAuthStore((st) => st.user);
  const bottomNav = bottomNavFor(navUser);
  // FM Manager rebuild C0.2: the FM's site picker and site-zone greeting.
  const fm = isFmExperience(navUser);
  // FM Technician rebuild C1: the technician's shell — its top-bar pieces,
  // the running-job bar, the .tech size on the page and a bigger phone bar.
  const tech = !fm && isTechShell(navUser);
  // Reception rebuild C4: the front desk's own top-bar pieces.
  const reception = !fm && !tech && isReceptionShell(navUser);
  // A desk's clock is its building's from the first paint; the top bar then names
  // the exact building once the desk's list has loaded (utils/timezone).
  if (reception && navUser?.day_timezone) setDeskTimeZone(navUser.day_timezone);
  const clock = useOrgClock();
  const techHome = useTechHome(tech);
  // B17: while the PIN lock is up nothing of the page is SHOWN or READ
  // (interpretation 160) — but it is not unmounted either (review R2-2): that
  // threw away whatever the technician had typed and not saved. It stays
  // mounted, React state and all, with display:none, aria-hidden and inert.
  // useTechLocked works the lock out at render time, so after a long absence
  // the page is never painted for a frame before the lock.
  const techLocked = useTechLocked(tech ? navUser?.id : null);
  const hideForLock = tech && techLocked;
  const shell = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = shell.current;
    if (!el) return;
    if (hideForLock) el.setAttribute('inert', '');
    else el.removeAttribute('inert');
  }, [hideForLock]);
  // Review R2-3 #5: nothing opens over or instead of the PIN lock, and no
  // shortcut moves the page behind it. The keyboard-driven pieces (Quick
  // Actions 'q', search Cmd+K and '/', shortcuts '?' and 'g d') are not
  // mounted while locked — their document listeners go with them — and a
  // search left open is closed, so it does not come back on unlock; the
  // Cmd+K listener below is not attached while locked.
  useEffect(() => {
    if (hideForLock) setSearchOpen(false);
  }, [hideForLock]);

  // Inactivity-based auto-logout (active users are kept signed in via silent
  // token refresh in utils/api.ts; this only fires after IDLE_TIMEOUT_MS idle).
  useIdleLogout();

  useEffect(() => {
    if (hideForLock) return undefined; // R2-3 #5: not under the PIN lock
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!((e.metaKey || e.ctrlKey) && e.key === 'k')) return;

      // Not while the user is typing. On macOS Ctrl+K is kill-to-end-of-line
      // in every text field, and this handler used to preventDefault() it
      // unconditionally — so the shortcut silently broke normal editing.
      // Cmd+K still opens search from anywhere, which is the combo Mac users
      // actually reach for.
      const target = e.target as HTMLElement | null;
      const isInput =
        !!target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable);
      if (isInput && !e.metaKey) return;

      e.preventDefault();
      setSearchOpen((prev) => !prev);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hideForLock]);


  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');
  // A bar item with a query (the FM's Approvals, /fm?approvals=1) is active
  // only while its query is in the URL; the plain item for that path is not.
  const queryOn = (search: string) =>
    new URLSearchParams(location.search).get(search.replace(/^\?/, '').split('=')[0] ?? '')
      === (search.split('=')[1] ?? '');
  const barActive = (item: { path: string; search?: string }) => {
    if (item.search) return location.pathname === item.path && queryOn(item.search);
    const covered = bottomNav.some((o) => o.search && o.path === item.path
      && location.pathname === o.path && queryOn(o.search));
    // The desk's Home is the parent of every desk page, and only Home.
    if (reception && item.path === RECEPTION_HOME) return location.pathname === RECEPTION_HOME;
    return !covered && isActive(item.path);
  };

  // FM Technician R2 "Me" (first run, PIN lock, offline pack, P1 sound) sits
  // OUTSIDE the page so it stays shown while the page is hidden by the lock.
  return (
    <>
    {tech && <TechMeEffects />}
    <div ref={shell} className="flex h-screen overflow-hidden bg-surface-subtle" role="application" aria-label="Spaciora"
         data-testid="app-shell" aria-hidden={hideForLock || undefined}
         style={hideForLock ? { display: 'none' } : undefined}>
      {/* Skip-to-content link (WCAG 2.1 - Feature #16) */}
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>
      {/* ----------------------------------------------------------------- */}
      {/* Desktop sidebar */}
      {/* ----------------------------------------------------------------- */}
      <div className="hidden lg:flex" data-sidebar role="navigation" aria-label="Main navigation">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => {
            const next = !sidebarCollapsed;
            setSidebarCollapsed(next);
            writeSidebarCollapsed(next);
          }}
        />
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* Mobile sidebar drawer */}
      {/* ----------------------------------------------------------------- */}
      <Transition show={mobileMenuOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setMobileMenuOpen}>
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-scrim/50" />
          </Transition.Child>

          {/* Panel */}
          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-[16rem]">
                {/* Close button */}
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setMobileMenuOpen(false)}>
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6 text-content-inverse" />
                    </button>
                  </div>
                </Transition.Child>

                <Sidebar
                  collapsed={false}
                  onToggle={() => {}}
                  onMobileClose={() => setMobileMenuOpen(false)}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* ----------------------------------------------------------------- */}
      {/* Main content area */}
      {/* ----------------------------------------------------------------- */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Above the header, and outside the scrolling <main>, so it cannot
            leave the screen while somebody is inside another person's
            account. Renders nothing for an ordinary session. */}
        <ImpersonationBanner />

        {/* A live emergency alert for a building this person is in. Outside
            the scrolling <main> for the same reason: it must not scroll away
            while it is live. Renders nothing when nothing is. */}
        <EmergencyBanner />

        {/* The privacy notice, at first login and again when it is revised.
            Renders nothing when the person has acknowledged the current
            version — and nothing at all when the organisation has published
            none, which is the honest state rather than a prompt about a
            placeholder. */}
        <NoticePrompt />
        {/* Employee rebuild C12: "turn on notifications for this device".
            Renders nothing unless turning push on would do something here. */}
        <PushPromptBar />
        {/* Top bar */}
        {/* FM (B23E): the Emergency button joins the row, so on a phone the gaps
            tighten and the search box may shrink — the avatar must not be pushed
            off the edge. Every other role's header is as it was. */}
        <header className={`flex h-16 items-center border-b border-line bg-surface px-4 lg:px-6 ${fm || tech || reception ? 'gap-2 sm:gap-4' : 'gap-4'}`} role="banner" aria-label="Top navigation bar">
          {/* Mobile menu button (Feature #15: WCAG aria-label) */}
          <button
            type="button"
            className="lg:hidden -m-2.5 p-2.5 text-content-muted"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open navigation menu"
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" />
          </button>

          {/* Search */}
          <div className={`flex flex-1 ${fm || tech || reception ? 'min-w-0' : ''}`} data-tour="search">
            {/* The technician's phone bar has no room for search (the
                prototype has none); it stays from lg, and on Cmd+K. */}
            <div className={`relative w-full max-w-md ${tech || reception ? 'hidden lg:block' : ''}`}>
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-content-subtle" />
              <input
                type="search"
                placeholder={narrowScreen ? 'Search…' : 'Search people, rooms, help (Cmd+K)…'}
                className="input-field pl-9 py-2 text-sm cursor-pointer"
                onClick={() => setSearchOpen(true)}
                
                readOnly
                aria-label="Open global search"
              />
            </div>
          </div>

          {/* Right side */}
          {/* The technician's row may shrink (min-w-0) so the connection pill
              truncates instead of pushing the bar into sideways scroll. */}
          <div className={`flex items-center ${fm ? 'gap-1.5 sm:gap-3' : tech ? 'min-w-0 gap-1.5 sm:gap-3' : reception ? 'min-w-0 gap-1 sm:gap-3' : 'gap-3'}`}>
            {/* FM scope (A11): renders for the two FM roles only. The FM
                Manager's is also the site picker (C0.2). */}
            {/* The technician's chip from sm: at 360 px the bar holds the
                Online pill, help, bell, SOS and the account menu (Sign out),
                and the home page names the site (R3 e2e F2). */}
            {/* The desk's top bar holds its building, connection, printer and
                bell, standing in for the chip, queue badge and notifications. */}
            {reception ? <DeskTopBar /> : fm ? <FmSitePicker /> : tech
              ? <span className="hidden sm:inline-flex" data-testid="tech-site-chip"><SiteScopeChip /></span>
              : <SiteScopeChip />}
            {fm && <FmGreeting />}
            {/* B23E: "⚠ Emergency" — always enabled; asks the site if it must. */}
            {fm && <FmEmergencyButton />}
            {/* C1: Online/Offline with the data's age and what waits to be
                sent (it stands in for the queue badge below). */}
            {tech && <TechConnectionPill dataUpdatedAt={techHome.dataUpdatedAt} />}
            {/* Version Badge (#18) */}
            <button
              type="button"
              onClick={() => setShowChangelog(true)}
              className="hidden sm:inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-700 hover:bg-brand-100 transition-colors"
              aria-label="View changelog"
              data-no-print
            >
              v2.4
            </button>

            {/* Clock — the organisation's zone, which is the zone of every
                timestamp below it (DSH-025). */}
            {!fm && <span
              data-testid="topbar-clock"
              title={`Organisation time: ${clock.iana}`}
              className="hidden sm:inline-flex items-center gap-1 rounded-lg bg-surface-muted px-2.5 py-1.5 text-xs font-medium text-content-tertiary"
            >
              {clock.time} <span className="text-content-subtle">{clock.zone}</span>
            </span>}

            {/* Work saved on this device that the server has not confirmed.
                Left of the bell deliberately: a notification is something that
                happened, this is something that has NOT happened yet, and the
                second is the one somebody needs to act on. Renders nothing
                when the queue is empty. */}
            {!tech && !reception && <PendingQueueBadge />}

            {/* Help — the top bar's "?" below lg; the floating one from lg. */}
            <SupportHeaderButton />

            {/* Notifications */}
            {<div data-tour="notifications">
              <NotificationCentre />
            </div>}

            {/* C1 / C14: the red Report incident (from sm; in the menu on a
                phone). B41 (R3): the SOS beside it — hold to send. */}
            {tech && <TechIncidentButton />}
            {tech && <TechSos />}

            {/* User menu — components/Layout/UserMenu (DSH-027: it carries My
                Profile and its tabs; the inline copy here offered only
                Settings and Sign out). */}
            <UserMenu />
          </div>
        </header>

        {/* B27: the one running job, with its timer, on every page. */}
        {tech && <RunningJobBar job={techHome.data?.running_job} />}

        {/* Page content */}
        {/* pb-20 at EVERY width (Bug_84): the "?" help button floats
            bottom-right on desktop too, and without room at the foot of the
            page it sat on the last row's right-hand controls — a table's
            "Next", a form's Save. Below lg the same padding clears the
            mobile nav, as it always did. */}
        <main id="main-content" className="flex-1 overflow-y-auto pb-20" role="main" aria-label="Page content">
          <VerifyPasswordBanner />
          {/* C1: the technician's pages at the .tech size (48 px controls,
              14 px text — DESIGN-CHECK §2), whichever page they are on. */}
          {tech ? <div className="tech" data-testid="tech-content"><Outlet /></div> : <Outlet />}
          {/* C34: what waits on this device, and what was refused, in words. */}
          {reception && <PendingQueueLine place="desk" />}
        </main>

        {/* ----------------------------------------------------------------- */}
        {/* Mobile bottom nav */}
        {/* ----------------------------------------------------------------- */}
        <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface lg:hidden" role="navigation" aria-label="Mobile navigation">
          <div className="flex items-center justify-around">
            {bottomNav.map((item) => {
              const Icon = item.icon;
              // The item with no path opens the menu drawer (the employee's
              // Menu): their remaining items live there, not in Settings.
              if (item.path === null) {
                return (
                  <button
                    key="menu"
                    type="button"
                    onClick={() => setMobileMenuOpen(true)}
                    data-testid="bottom-nav-menu"
                    className={`flex flex-1 flex-col items-center gap-0.5 py-2 font-medium text-content-subtle ${
                      tech ? 'min-h-12 text-sm' : 'text-[10px]'}`}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </button>
                );
              }
              const active = barActive({ path: item.path, search: item.search });
              return (
                <Link
                  key={`${item.path}${item.search ?? ''}`}
                  to={`${item.path}${item.search ?? ''}`}
                  aria-current={active ? 'page' : undefined}
                  data-testid={`bottom-nav-${item.label.toLowerCase()}`}
                  // The technician's bar: 48 px and 14 px (DESIGN-CHECK §1).
                  className={`flex flex-1 flex-col items-center gap-0.5 py-2 font-medium ${
                    tech ? 'min-h-12 text-sm' : 'text-[10px]'} ${
                    active ? 'text-brand-700' : 'text-content-subtle'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
          {/* Safe area for iPhone notch */}
          <div className="h-safe-area-inset-bottom bg-surface" />
        </nav>
      </div>

      {/* 🔴 THE SUPPORT BUTTON IS BACK, BECAUSE THERE IS SOMETHING TO SEND TO.

          It was unmounted for months and the reason was the RECIPIENT, not the
          handler: it promised a human reply within 24 hours to a message it
          threw away — zero calls on the wire, verified on the deployed build —
          and there was no route, no inbox and no address anywhere in this
          codebase for a handler to have used.

          POST /api/support/threads is that recipient. The message is stored in
          the product and read by a super admin in the support inbox, and the
          reply comes back into this same panel. Nothing waits on email: the
          thread is the record and the dot on the button is the marker.

          The copy promises a mechanism and never a time. */}
      <SupportModal />

      {/* Not under the PIN lock (review R2-3 #5). */}
      {!hideForLock && (
        <>
          {/* Quick Actions (Q key) */}
          <QuickActions onOpenSearch={() => setSearchOpen(true)} />

          {/* Global Search (Cmd+K) */}
          <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

          {/* Keyboard Shortcuts (?) */}
          <KeyboardShortcuts onOpenSearch={() => setSearchOpen(true)} />

          {/* First visit. The employee experience gets the five-step welcome,
              remembered on the server (Employee rebuild C1); every other role
              keeps the tour of the staff shell it was written for. Not over
              the lock either: it would open on its own timer. */}
          {isEmployeeExperience(navUser) ? <EmployeeWelcome /> : <OnboardingTour />}
        </>
      )}

      {/* Changelog Modal (#18) */}
      <Transition show={showChangelog} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowChangelog(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-scrim/40" />
          </Transition.Child>
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg rounded-xl bg-surface p-6 shadow-xl">
                <Dialog.Title className="text-lg font-semibold text-content mb-1">
                  What&apos;s New in v2.4
                </Dialog.Title>
                <p className="text-xs text-content-subtle mb-4">Released April 2026</p>
                <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-2">
                  {[
                    'FIFO/LIFO inventory valuation for parts',
                    'Sub-work-orders with parent completion tracking',
                    'Work order templates (10 pre-built)',
                    'Checklist builder with drag-drop fields',
                    'Fire drill scheduler with 30-day notifications',
                    'Statutory inspection calendar',
                    'GreenPro/GEM certification tracking',
                    'BIS standards reference linked to PPM checklists',
                    'Insurance valuation PDF report',
                    'Government asset format export (GFR/CPWD)',
                    'SFTP export/import configuration',
                    'Integration marketplace with 12 connectors',
                    'Catering provider marketplace',
                    'Print CSS for all reports',
                    'WCAG accessibility improvements',
                    'Help tooltips on complex fields',
                    'Skeleton loading states',
                    'Enhanced empty states per module',
                    'Copy button for IDs and codes',
                    'Right-click context menus on tables',
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm text-content-tertiary">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-brand-500 flex-shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowChangelog(false)}
                    className="btn-primary"
                  >
                    Got it
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
    </>
  );
}
